/**
 * BriefingCTABlock — premium editorial CTA block built exactly to spec.
 *
 *  ┌──────────────────────────────────────────┐
 *  │ SECTION LABEL                            │
 *  │ "Monthly Briefing"                       │
 *  │                                          │
 *  │ Headline                                 │
 *  │ "Access this month's private signals"    │
 *  │                                          │
 *  │ Supporting copy                          │
 *  │ 1–2 short lines explaining the value     │
 *  │ and who it is for.                       │
 *  │                                          │
 *  │ [ PRIMARY CTA BUTTON ]                   │
 *  │ "Access the Full Briefing"               │
 *  │                                          │
 *  │ Optional secondary line                  │
 *  │ "Prefer a tailored partnership note?"    │
 *  │ [ Secondary text link ]                  │
 *  │ "Request the Media Kit"                  │
 *  └──────────────────────────────────────────┘
 *
 * Design rules:
 *  - Cormorant Garamond + Montserrat + Open Sans (loaded by .newsletter-theme parent)
 *  - One primary action only, visually dominant
 *  - Generous whitespace, dark luxe background, single brass accent
 *  - 48px min-height tap target, sentence case, 2-4 word button label
 *  - Mobile-first: full-width button below ~480px, centered on desktop
 */

import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function BriefingCTABlock({
  sectionLabel = "Monthly Briefing",
  headline = "Access this month's private signals.",
  supportingCopy = "Curated for UHNW earners, family offices, advisors, and luxury-market partners — quiet signal, not noise.",
  primaryHref = "/newsletter#subscribe",
  primaryLabel = "Access the Full Briefing",
  secondaryPrompt = "Prefer a tailored partnership note?",
  secondaryHref = "/contact?type=media-kit",
  secondaryLabel = "Request the Media Kit",
  variant = "dark", // "dark" | "neutral"
  testid = "briefing-cta-block",
}) {
  const bg = variant === "dark" ? "var(--bg)" : "var(--surface)";
  return (
    <section
      className="newsletter-theme px-6 py-20 md:py-24"
      style={{ background: bg, borderTop: "1px solid var(--hairline)", borderBottom: "1px solid var(--hairline)" }}
      data-testid={testid}
    >
      <div className="max-w-2xl mx-auto" style={{ textAlign: "center" }}>
        {/* SECTION LABEL */}
        <p
          className="newsletter-eyebrow mb-6"
          style={{ color: "var(--brass)" }}
          data-testid={`${testid}-label`}
        >
          {sectionLabel}
        </p>

        {/* HEADLINE */}
        <h2
          className="newsletter-display leading-[0.95] text-4xl md:text-5xl mb-6"
          style={{ color: "var(--ink)" }}
          data-testid={`${testid}-headline`}
        >
          {headline}
        </h2>

        {/* SUPPORTING COPY */}
        <p
          className="newsletter-body mx-auto mb-10"
          style={{
            color: "var(--ink-muted)",
            fontSize: 16,
            lineHeight: 1.65,
            maxWidth: 520,
          }}
          data-testid={`${testid}-supporting`}
        >
          {supportingCopy}
        </p>

        {/* PRIMARY CTA */}
        <div className="flex justify-center">
          <Link
            to={primaryHref}
            className="btn-brass"
            data-testid={`${testid}-primary`}
            style={{
              minHeight: 48,
              padding: "14px 28px",
              fontSize: 13,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 600,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              minWidth: 240,
              justifyContent: "center",
            }}
          >
            {primaryLabel}
            <ArrowRight size={14} />
          </Link>
        </div>

        {/* SECONDARY (OPTIONAL) */}
        {(secondaryPrompt || secondaryLabel) && (
          <div className="mt-10" data-testid={`${testid}-secondary-block`}>
            <p
              className="newsletter-body mb-2"
              style={{ color: "var(--ink-muted)", fontSize: 13 }}
            >
              {secondaryPrompt}
            </p>
            <Link
              to={secondaryHref}
              className="newsletter-eyebrow"
              data-testid={`${testid}-secondary`}
              style={{
                color: "var(--brass)",
                fontSize: 11,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                textDecoration: "none",
                borderBottom: "1px solid var(--brass)",
                paddingBottom: 2,
                display: "inline-block",
              }}
            >
              {secondaryLabel}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
